import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaUsers, FaUserCheck, FaUserTimes } from "react-icons/fa";
import { axiosMaster, axiosSalary } from "../Components/api/axiosInstance";
import { useAuth } from "../Components/auth/AuthContext";

/* ═══════════════════════════════════════════════════
   DATE UTILITY — YYYYMMDD integer, no timezone issues
   ═══════════════════════════════════════════════════ */
const toDateInt = (str) => {
  if (!str) return null;
  const s = String(str);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return parseInt(s.slice(0, 10).replace(/-/g, ""), 10);
  if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) {
    const [dd, mm, yyyy] = s.split("/");
    return parseInt(`${yyyy}${mm}${dd}`, 10);
  }
  return null;
};

/* ═══════════════════════════════════════════════════
   isActiveOnDate
   Business Rule:
     relievingDate = null       → currently working → show ✅
     relievingDate has any date → employee left     → hide ❌
   ═══════════════════════════════════════════════════ */
const isActiveOnDate = (emp, reportDateStr) => {
  const rd = toDateInt(reportDateStr);
  if (!rd) return false;

  const tenures =
    Array.isArray(emp.employmentHistory) && emp.employmentHistory.length > 0
      ? emp.employmentHistory
      : [{ joiningDate: emp.joiningDate, relievingDate: emp.relievingDate ?? null }];

  return tenures.some((tenure) => {
    const join    = toDateInt(tenure.joiningDate);
    const relieve = toDateInt(tenure.relievingDate);
    if (!join) return false;
    if (relieve) return false;          // has relievingDate → left → hide
    return join <= rd;                  // joined on or before today → show
  });
};

/* ═══════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════ */
const DashboardHome = () => {

  /* ─── AUTH ─── */
  const { user } = useAuth();
  const groupId      = user?.groupId;
  const userTypeId   = user?.userTypeId;
  const restaurantId = user?.restaurantId;

  /* ─── STATE ─── */
  const [employeeData, setEmployeeData] = useState([]);
  const [presentData,  setPresentData]  = useState([]);
  const [loading,      setLoading]      = useState(false);

  /* ─── TODAY as YYYYMMDD ─── */
  const todayStr = new Date().toISOString().split("T")[0]; // "2026-06-23"

  /* ─── FETCH ─────────────────────────────────────────────────────────
     Employee master + attendance in one Promise.all.
     Employees filtered by:
       1. No relievingDate (currently working)
       2. joiningDate on or before today
       3. Restaurant match for userTypeId 4
  ─────────────────────────────────────────────────────────────────── */
  const fetchDashboard = useCallback(async () => {
    if (!groupId) return;
    try {
      setLoading(true);

      const [empRes, attRes] = await Promise.all([
        axiosMaster.get(`/EmployeeMaster/GetByGroup?groupId=${groupId}`),
        axiosSalary.get(`/AttendanceLog/GetPresentTodayByGroup?groupId=${groupId}`),
      ]);

      /* --- Employees: active today only --- */
      let empList = (empRes.data || []).filter((emp) =>
        isActiveOnDate(emp, todayStr)
      );

      if (userTypeId === 4 && restaurantId) {
        empList = empList.filter(
          (emp) => emp.activeRestId === restaurantId || emp.restId === restaurantId
        );
      }

      setEmployeeData(empList);

      /* --- Present --- */
      let presentList = attRes.data || [];
      if (userTypeId === 4 && restaurantId) {
        presentList = presentList.filter((item) => item.restId === restaurantId);
      }

      setPresentData(presentList);

    } catch (err) {
      console.error("Dashboard API error:", err);
    } finally {
      setLoading(false);
    }
  }, [groupId, userTypeId, restaurantId, todayStr]);

  /* ─── AUTO REFRESH every 30s ─── */
  useEffect(() => {
    if (!groupId) return;
    fetchDashboard();
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchDashboard();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  /* ─── CALCULATIONS ─── */

  // Active employee ID set (relieved employees already excluded above)
  const employeeIdSet = useMemo(
    () => new Set(employeeData.map((emp) => emp.empId)),
    [employeeData]
  );

  // Valid + unique present IDs (cross-checked against active employees)
  const presentIds = useMemo(() => {
    const set = new Set();
    presentData.forEach((p) => {
      if (employeeIdSet.has(p.empId)) set.add(p.empId);
    });
    return set;
  }, [presentData, employeeIdSet]);

  const totalEmployees = employeeIdSet.size;
  const presentCount   = presentIds.size;
  const absentCount    = Math.max(totalEmployees - presentCount, 0);

  /* ─── STATS ─── */
  const stats = [
    {
      title: "Total Employees",
      value: totalEmployees,
      icon:  FaUsers,
      color: "from-blue-500 to-indigo-500",
    },
    {
      title: "Present Today",
      value: presentCount,
      icon:  FaUserCheck,
      color: "from-green-500 to-emerald-500",
    },
    {
      title: "Absent Today",
      value: absentCount,
      icon:  FaUserTimes,
      color: "from-red-500 to-rose-500",
    },
  ];

  /* ═══════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════ */
  return (
    <div className="space-y-6">

      {/* ── HEADER ── */}
      <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg p-6 border">
        <h2 className="text-2xl font-bold text-slate-800">
          Dashboard Overview
        </h2>
        <p className="text-slate-500 mt-1">
          Welcome to{" "}
          <span className="font-semibold">BillTrace Attendance System</span>
        </p>
        {loading && (
          <p className="text-xs text-gray-400 mt-2">Updating data...</p>
        )}
      </div>

      {/* ── STATS ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
        {stats.map((item, index) => {
          const Icon = item.icon;
          return (
            <div
              key={index}
              className="bg-white/80 backdrop-blur-md rounded-2xl shadow-md p-5 border hover:shadow-xl transition"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{item.title}</p>
                  <h3 className="text-2xl font-bold text-slate-800 mt-1">
                    {item.value}
                  </h3>
                </div>
                <div className={`p-3 rounded-xl text-white bg-gradient-to-r ${item.color}`}>
                  <Icon size={20} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default DashboardHome;